import { arrayToMap } from 'lib/array/arrayUtils'
import type { TripDocumentsResult } from 'tripPlanner/hooks/api/documents'
import type { FullTrip } from 'tripPlanner/types/common'
import type { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'
import type { TripItem } from 'tripPlanner/types/tripItem'

interface DocumentDisplayLabels {
  flightLabel: string
  noteLabel: string
}

const DEFAULT_LABELS: DocumentDisplayLabels = {
  flightLabel: 'flight',
  noteLabel: 'Note',
}

export function flattenDocuments(
  trip: FullTrip,
  documents: TripDocumentsResult,
  labels?: DocumentDisplayLabels,
): Array<TripDocumentWithItemInfo> {
  const indexedItems = arrayToMap(trip.items, (item) => item.id)
  const attachedFiles = Object.entries(documents.attachedFiles).flatMap(
    ([itemId, files]) => {
      const item = indexedItems.get(itemId)
      if (item) {
        return files.map((file) => ({
          ...file,
          item: { id: item.id, name: getAttachedItemDisplayName(item, labels) },
        }))
      }
      return files
    },
  )
  return [...attachedFiles, ...documents.unAttachedFiles]
}

export function getAttachedItemDisplayName(item: TripItem, labels: DocumentDisplayLabels = DEFAULT_LABELS): string {
  if (item.type === 'FLIGHT') {
    return `${item.originAirportCode} - ${item.destinationAirportCode} ${labels.flightLabel}`
  }
  if (item.type === 'NOTE') {
    return item.title || labels.noteLabel
  }
  if ('name' in item && item.name) {
    return item.name
  }
  if ('place' in item && item.place.name) {
    return item.place.name
  }
  return item.sortableName
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
